/**
 * 极薄 fetch wrapper。
 *
 * 职责：
 *   * 拼 base URL（dev 走 vite proxy `/api`，单机版 Tauri 走 127.0.0.1:8080，云版走 api.vip0101.com）
 *   * 自动塞 Authorization Bearer
 *   * 错误统一抛 ApiError（含 status + message + 后端 error_code）
 *
 * **不**做：
 *   * 不做 retry —— retry 是业务决定（上传失败用户决定，搜图自动重试无意义）
 *   * 不做 cache —— pinia store 自己管
 *   * 不做 abort signal helper —— component 用 AbortController 透传
 */

import { useAuthStore } from '@/stores/auth'

/** 后端错误响应的标准 shape（fabric-server error.rs 的 wire format）。*/
export interface ApiErrorBody {
  error: string
  message: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

interface RequestInitExt extends Omit<RequestInit, 'body'> {
  /** 普通 JSON body —— 自动 stringify + Content-Type。multipart 用 multipartBody。*/
  jsonBody?: unknown
  multipartBody?: FormData
  /** query string 参数 —— 数字会 toString，null/undefined 跳过。*/
  query?: Record<string, string | number | boolean | null | undefined>
  /** 跳过 401 → 全局登出/重登引导（P1-6）。登录本身的 401 是"账号密码错"、
   *  `/auth/me` 对设备令牌的 401 是"免费档非用户会话"——都不是令牌过期,不该清登录态。*/
  skipAuthRedirect?: boolean
}

// 默认相对 /api/v1(dev vite proxy / 3a Caddy 同源);Tauri 构建注入绝对地址
// (单机版 → 本机 sidecar;云版 → api.vip0101.com)。
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

function buildUrl(path: string, query?: RequestInitExt['query']): string {
  const url = `${API_BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined) continue
    params.append(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

export async function apiRequest<T>(path: string, init: RequestInitExt = {}): Promise<T> {
  const auth = useAuthStore()
  const headers = new Headers(init.headers)
  // desktop-login P1：身份走用户登录令牌（Authorization: Bearer），服务端从令牌
  // 解出租户+角色。登录前（/auth/login 本身）无令牌,公开端点不需要。
  if (auth.token) {
    headers.set('Authorization', `Bearer ${auth.token}`)
  }

  let body: BodyInit | undefined
  if (init.jsonBody !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(init.jsonBody)
  } else if (init.multipartBody !== undefined) {
    // FormData 让浏览器自动设 boundary —— **绝对不要** 手动设
    // Content-Type，否则 boundary 缺失，后端解析失败。
    body = init.multipartBody
  }

  const resp = await fetch(buildUrl(path, init.query), {
    method: init.method ?? 'GET',
    headers,
    body,
    signal: init.signal
  })

  if (!resp.ok) {
    // 令牌过期(401,非登录/身份自检接口):统一清登录态 + 引导重登。并发 401 只触发一次
    // (store 内部去重)。403 / 业务错误(套餐未开通、停服等)不走这里,交调用方按 code 处理。
    if (resp.status === 401 && !init.skipAuthRedirect) {
      auth.onUnauthorized()
    }
    let code = 'unknown'
    let message = resp.statusText
    try {
      const data = (await resp.json()) as ApiErrorBody
      code = data.error ?? code
      message = data.message ?? message
    } catch {
      // body 不是 JSON，沿用 statusText
    }
    throw new ApiError(resp.status, code, message)
  }

  // 204 No Content 用 null 兜底，调用方按需 cast。
  if (resp.status === 204) return null as T
  return (await resp.json()) as T
}

/**
 * 下载文件(CSV 导出等):带鉴权 fetch → blob → 锚点触发浏览器下载。
 * 文件名由调用方定(后端不发 Content-Disposition,中文名走 download 属性最稳)。
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const auth = useAuthStore()
  const headers = new Headers()
  if (auth.token) {
    headers.set('Authorization', `Bearer ${auth.token}`)
  }
  const resp = await fetch(buildUrl(path), { headers })
  if (!resp.ok) {
    // 导出都是已登录业务接口,401 = 令牌过期 → 同 apiRequest 走全局重登引导(P1-6)。
    if (resp.status === 401) {
      auth.onUnauthorized()
    }
    let code = 'unknown'
    let message = resp.statusText
    try {
      const data = (await resp.json()) as ApiErrorBody
      code = data.error ?? code
      message = data.message ?? message
    } catch {
      // body 不是 JSON，沿用 statusText
    }
    throw new ApiError(resp.status, code, message)
  }
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
